import React from "react";
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableCellProps,
    TableContainer,
    TableHead,
    TableRow,
    tableCellClasses,
    styled,
} from "@mui/material";

export interface StyledTableColumn<T> {
    key: string;
    header?: React.ReactNode;
    render: (row: T, rowIndex: number) => React.ReactNode;
    cellProps?: TableCellProps;
    clickable?: boolean;
}

export interface StyledTableProps<T> {
    columns: StyledTableColumn<T>[];
    rows: T[];
    getRowKey: (row: T, index: number) => React.Key;
    size?: "small" | "medium";
    showHeader?: boolean;
    onCellClicked?: (row: T, cellIndex: number) => void;
}

const RoundedPaper = styled(Paper)(({theme}) => ({
    borderRadius: theme.shape.borderRadius * 2,
    overflow: "hidden",
}));

const StyledHeadRow = styled(TableRow)(({theme}) => ({
    [`& .${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        fontWeight: 600,
    },
}));

const StyledBodyRow = styled(TableRow)(({theme}) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
    "&:nth-of-type(even)": {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
    },
    "&:hover": {
        filter: "brightness(0.95)",
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
    transition: "filter 0.15s ease-in-out",
}));

export function StyledTable<T>({columns, rows, getRowKey, size = "small", showHeader = true, onCellClicked}: StyledTableProps<T>) {
    return (
        <TableContainer component={RoundedPaper} elevation={3}>
            <Table size={size}>
                {showHeader && (
                    <TableHead>
                        <StyledHeadRow>
                            {columns.map((column) => (
                                <TableCell key={column.key}>{column.header}</TableCell>
                            ))}
                        </StyledHeadRow>
                    </TableHead>
                )}
                <TableBody>
                    {rows.map((row, rowIndex) => (
                        <StyledBodyRow key={getRowKey(row, rowIndex)}>
                            {columns.map((column, cellIndex) => {
                                const isClickable = !!onCellClicked && column.clickable !== false;
                                return (
                                    <TableCell
                                        key={`${getRowKey(row,rowIndex)}-${column.key}`}
                                        {...column.cellProps}
                                        onClick={isClickable ? () => onCellClicked(row, cellIndex) : column.cellProps?.onClick}
                                        sx={isClickable ? (theme) => ({
                                            cursor: "pointer",
                                            textDecoration: "underline dotted",
                                            textUnderlineOffset: theme.spacing(0.5),
                                            "&:hover": {backgroundColor: theme.palette.action.hover},
                                        }) : column.cellProps?.sx}
                                    >
                                        {column.render(row, rowIndex)}
                                    </TableCell>
                                );
                            })}
                        </StyledBodyRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
